import type { AxiosInstance, AxiosResponse } from "axios";
import { Platform } from "react-native";
import FormData from "form-data";
import { notify } from "@lib/util/logger";

type ApiResponse<T = object> = {
  errors?: Array<{
    message: string;
    locations: Array<{ [index: string]: unknown }>;
    path: unknown[];
    extensions: {
      code: string;
    };
  }>;
  data: T;
};

type RNFileType = { uri: string; name: string; type: string };

interface ExecuteIOSFormDataUploadParams {
  uploadables: Record<string, unknown>;
  apiEndpoint: string;
  formData: FormData;
  headers: Record<string, string>;
  axiosInstance: AxiosInstance;
  appVersion?: string;
}

/**
 * iOS環境でのFormData送信に関する追加のフォールバック戦略を実装した関数
 *
 * この関数は、RelayEnvironment.tsの既存処理（transformRequestワークアラウンドと標準POST）が
 * 失敗した場合のみ呼び出され、以下の10つの追加戦略を順次試行:
 * 1. file:// プレフィックス付き + transformRequest ワークアラウンド
 * 2. file:// プレフィックス付き + 標準的な方法
 * 3. FormData再構築 + 標準的な方法
 * 4. Content-Typeヘッダー削除（Issue #1321）
 * 5. maxBodyLength/maxContentLength無制限（Issue #1567）
 * 6. 手動バウンダリー設定（Issue #4406）
 * 7. Fetch API使用（Issue #1321）
 * 8. XMLHttpRequest使用（Community Recommended - React Native #44657, #30071）
 * 9. URI形式正規化（iOS triple-slash対応 - React Native #5308）
 * 10. 強化されたMIME型検出 + 再構築（最終フォールバック）
 *
 * 参照:
 * - https://github.com/axios/axios/issues/1321, #1567, #4406
 * - https://github.com/facebook/react-native/issues/44657, #30071, #5308, #25790
 *
 * @param params - アップロードパラメータ
 * @returns API レスポンス
 */
export async function tryIOSFormDataUploadFallback(
  params: ExecuteIOSFormDataUploadParams
): Promise<AxiosResponse<ApiResponse>> {
  const {
    uploadables,
    apiEndpoint,
    formData,
    headers,
    axiosInstance,
    appVersion,
  } = params;

  const strategies = [
    {
      name: "Strategy 1: file:// prefix + transformRequest",
      execute: async () => {
        // file:// プレフィックスが付いているか確認し、必要なら追加
        const modifiedFormData = await ensureFilePrefixInFormData(
          formData,
          true
        );
        return axiosInstance.post(apiEndpoint, modifiedFormData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers,
          transformRequest: [() => modifiedFormData],
        });
      },
    },
    {
      name: "Strategy 2: file:// prefix + standard",
      execute: async () => {
        const modifiedFormData = await ensureFilePrefixInFormData(
          formData,
          true
        );
        return axiosInstance.post(apiEndpoint, modifiedFormData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers,
        });
      },
    },
    {
      name: "Strategy 3: Rebuild FormData + standard",
      execute: async () => {
        // FormDataを完全に再構築
        const rebuiltFormData = await rebuildFormData(formData);
        return axiosInstance.post(apiEndpoint, rebuiltFormData, {
          timeout: 1800000,
          headers,
        });
      },
    },
    {
      name: "Strategy 4: No Content-Type header (Issue #1321)",
      execute: async () => {
        // Content-Typeヘッダーを完全に削除してブラウザに任せる
        const modifiedFormData = await ensureFilePrefixInFormData(
          formData,
          true
        );
        const headersWithoutContentType = { ...headers };
        delete headersWithoutContentType["Content-Type"];
        return axiosInstance.post(apiEndpoint, modifiedFormData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers: headersWithoutContentType,
        });
      },
    },
    {
      name: "Strategy 5: Increased limits (Issue #1567)",
      execute: async () => {
        // maxBodyLengthとmaxContentLengthを無制限に設定
        const modifiedFormData = await ensureFilePrefixInFormData(
          formData,
          true
        );
        return axiosInstance.post(apiEndpoint, modifiedFormData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        });
      },
    },
    {
      name: "Strategy 6: Manual boundary + no transformRequest",
      execute: async () => {
        // バウンダリーを手動で設定（Issue #4406対策）
        const modifiedFormData = await ensureFilePrefixInFormData(
          formData,
          true
        );
        const boundary = `----FormDataBoundary${Date.now()}`;
        const headersWithBoundary = {
          ...headers,
          "Content-Type": `multipart/form-data; boundary=${boundary}`,
        };
        return axiosInstance.post(apiEndpoint, modifiedFormData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers: headersWithBoundary,
        });
      },
    },
    {
      name: "Strategy 7: Fetch API fallback (Issue #1321)",
      execute: async () => {
        // Axiosではなくfetch APIを使用
        const modifiedFormData = await ensureFilePrefixInFormData(
          formData,
          true
        );
        const token = headers.Authorization ?? "";
        const response = await fetch(apiEndpoint, {
          method: "POST",
          body: modifiedFormData as unknown as BodyInit,
          headers: {
            Authorization: token,
            AppVersion: appVersion ?? "unknown",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Fetch API failed: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        // Fetch APIのHeadersをAxios互換の形式に変換
        const axiosHeaders: Record<string, string> = {};
        response.headers.forEach((value: string, key: string) => {
          axiosHeaders[key] = value;
        });

        // Axiosレスポンス形式に変換
        return {
          data,
          status: response.status,
          statusText: response.statusText,
          headers: axiosHeaders,
          config: {},
        } as unknown as AxiosResponse<ApiResponse>;
      },
    },
    {
      name: "Strategy 8: XMLHttpRequest (Community Recommended)",
      // eslint-disable-next-line arrow-body-style
      execute: async () => {
        // XMLHttpRequestを使用（React Native #44657, #30071で推奨）
        // 多くのコミュニティメンバーがAxios/FetchよりXHRの方が
        // React NativeのFormDataと互換性が高いと報告
        const modifiedFormData = await ensureFilePrefixInFormData(
          formData,
          true
        );
        return new Promise<AxiosResponse<ApiResponse>>((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.onload = () => {
            try {
              if (xhr.status >= 200 && xhr.status < 300) {
                const responseData = JSON.parse(
                  xhr.responseText
                ) as ApiResponse;

                // XMLHttpRequestレスポンスをAxios形式に変換
                const responseHeaders: Record<string, string> = {};
                const headersStr = xhr.getAllResponseHeaders();
                headersStr.split("\r\n").forEach((line) => {
                  const [key, value] = line.split(": ");
                  if (key != null && value != null) {
                    responseHeaders[key] = value;
                  }
                });

                resolve({
                  data: responseData,
                  status: xhr.status,
                  statusText: xhr.statusText,
                  headers: responseHeaders,
                  config: {},
                } as unknown as AxiosResponse<ApiResponse>);
              } else {
                reject(
                  new Error(`XHR failed: ${xhr.status} ${xhr.statusText}`)
                );
              }
            } catch (error) {
              reject(
                error instanceof Error
                  ? error
                  : new Error("XHR response parse error")
              );
            }
          };

          xhr.onerror = () => {
            reject(new Error("XHR Network Error"));
          };

          xhr.ontimeout = () => {
            reject(new Error("XHR Timeout"));
          };

          xhr.open("POST", apiEndpoint);
          xhr.timeout = 1800000; // 30分

          // ヘッダーを設定（Content-Typeは除外してXHRに任せる）
          Object.entries(headers).forEach(([key, value]) => {
            if (key !== "Content-Type") {
              xhr.setRequestHeader(key, value);
            }
          });

          // FormDataをそのまま送信（JSON.stringifyしない）
          xhr.send(
            modifiedFormData as unknown as
              | Document
              | XMLHttpRequestBodyInit
              | null
          );
        });
      },
    },
    {
      name: "Strategy 9: Normalized URI format (iOS triple-slash)",
      execute: async () => {
        // iOSの file:/// 形式（3スラッシュ）を正規化
        // React Native #5308で報告されているURI形式の問題に対応
        const normalizedFormData = await normalizeURIFormat(formData);
        return axiosInstance.post(apiEndpoint, normalizedFormData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        });
      },
    },
    {
      name: "Strategy 10: Enhanced MIME detection + rebuild",
      execute: async () => {
        // 強化されたMIME型検出とFormData再構築（最終フォールバック）
        // iOS file pickerがMIME typeを正しく提供しない問題に対応
        const enhancedFormData =
          await rebuildFormDataWithMIMEDetection(formData);
        return axiosInstance.post(apiEndpoint, enhancedFormData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          transformRequest: [() => enhancedFormData],
        });
      },
    },
  ];

  let lastError: Error | null = null;

  // 各戦略を順次試行（awaitとcontinueは意図的に使用）
  /* eslint-disable no-await-in-loop, no-continue */
  for (let i = 0; i < strategies.length; i += 1) {
    const strategy = strategies[i];

    try {
      await notify(
        {
          type: "iOS FormData Upload Strategy Attempt",
          searchableType: "IOS_FORMDATA_STRATEGY_ATTEMPT",
          details: {
            strategyIndex: i + 1,
            strategyName: strategy.name,
            uploadablesCount: Object.keys(uploadables).length,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "debug", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      const response: AxiosResponse<ApiResponse> = await strategy.execute();

      // 成功ログ
      await notify(
        {
          type: "iOS FormData Upload Strategy Success",
          searchableType: "IOS_FORMDATA_STRATEGY_SUCCESS",
          details: {
            strategyIndex: i + 1,
            strategyName: strategy.name,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "info", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      await notify(
        {
          type: "iOS FormData Upload Strategy Failed",
          searchableType: "IOS_FORMDATA_STRATEGY_FAILED",
          details: {
            strategyIndex: i + 1,
            strategyName: strategy.name,
            errorMessage: lastError.message,
            willRetry: i < strategies.length - 1,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "warning", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      // 最後の戦略でなければ次の戦略を試行
      if (i < strategies.length - 1) {
        continue;
      }
    }
  }
  /* eslint-enable no-await-in-loop, no-continue */

  // すべての戦略が失敗した場合
  await notify(
    {
      type: "iOS FormData Upload All Strategies Failed",
      searchableType: "IOS_FORMDATA_ALL_STRATEGIES_FAILED",
      details: {
        totalStrategies: strategies.length,
        lastError: lastError?.message ?? "Unknown error",
        platform: Platform.OS,
        timestamp: new Date().toISOString(),
        version: appVersion,
      },
    },
    { level: "error", skipDeviceInfo: true }
  ).catch(() => {
    // 通知エラーは無視
  });

  throw lastError ?? new Error("All upload strategies failed");
}

/**
 * FormData内のファイルURIに file:// プレフィックスを追加/削除する
 *
 * @param formData - 元のFormData
 * @param shouldHavePrefix - file:// プレフィックスを付けるかどうか
 * @returns 修正されたFormData
 */
async function ensureFilePrefixInFormData(
  formData: FormData,
  shouldHavePrefix: boolean
): Promise<FormData> {
  try {
    const newFormData = new FormData();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formDataWithParts = formData as any;
    if (
      Platform.OS !== "web" &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      formDataWithParts._parts != null &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      Array.isArray(formDataWithParts._parts)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const parts = formDataWithParts._parts as Array<[string, any]>;
      parts.forEach(([key, value]) => {
        // ファイルオブジェクトの場合はURIを修正
        if (
          typeof value === "object" &&
          value != null &&
          "uri" in value &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          typeof value.uri === "string"
        ) {
          const file = value as RNFileType;
          const { uri: originalUri } = file;
          let uri = originalUri;

          if (
            shouldHavePrefix &&
            !uri.startsWith("file://") &&
            !uri.startsWith("http://") &&
            !uri.startsWith("https://")
          ) {
            // file:// プレフィックスを追加（存在しない場合）
            uri = `file://${uri}`;
          } else if (!shouldHavePrefix && uri.startsWith("file://")) {
            // file:// プレフィックスを削除（存在する場合）
            uri = uri.replace("file://", "");
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          newFormData.append(
            key,
            { ...file, uri } as RNFileType,
            file.name ?? "unknown"
          );
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          newFormData.append(key, value);
        }
      });
    }

    return newFormData;
  } catch (error) {
    // エラーが発生した場合は元のFormDataをそのまま返す
    await notify(
      {
        type: "ensureFilePrefixInFormData Error",
        searchableType: "ENSURE_FILE_PREFIX_ERROR",
        error: {
          message: error instanceof Error ? error.message : String(error),
          shouldHavePrefix,
        },
      },
      { level: "warning", skipDeviceInfo: true }
    ).catch(() => {
      // 通知エラーは無視
    });
    return formData;
  }
}

/**
 * FormDataを完全に再構築する
 *
 * @param formData - 元のFormData
 * @returns 再構築されたFormData
 */
async function rebuildFormData(formData: FormData): Promise<FormData> {
  try {
    const newFormData = new FormData();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formDataWithParts = formData as any;
    if (
      Platform.OS !== "web" &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      formDataWithParts._parts != null &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      Array.isArray(formDataWithParts._parts)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const parts = formDataWithParts._parts as Array<[string, any]>;
      parts.forEach(([key, value]) => {
        if (key === "operations" || key === "map") {
          // operations と map はそのまま追加
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          newFormData.append(key, value);
        } else if (
          typeof value === "object" &&
          value != null &&
          "uri" in value
        ) {
          // ファイルオブジェクトを再構築
          const file = value as RNFileType;
          const { uri: originalUri } = file;
          let uri = originalUri;

          // 複数のパターンを試す準備として、まずは file:// 付きで試行
          if (
            !uri.startsWith("file://") &&
            !uri.startsWith("http://") &&
            !uri.startsWith("https://")
          ) {
            uri = `file://${uri}`;
          }

          // 新しいファイルオブジェクトを作成
          const newFile: RNFileType = {
            uri,
            name: file.name ?? `file_${Date.now()}.bin`,
            type: file.type ?? "application/octet-stream",
          };

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
          newFormData.append(key, newFile as any, newFile.name);
        }
      });
    }

    return newFormData;
  } catch (error) {
    // エラーが発生した場合は元のFormDataをそのまま返す
    await notify(
      {
        type: "rebuildFormData Error",
        searchableType: "REBUILD_FORMDATA_ERROR",
        error: {
          message: error instanceof Error ? error.message : String(error),
        },
      },
      { level: "warning", skipDeviceInfo: true }
    ).catch(() => {
      // 通知エラーは無視
    });
    return formData;
  }
}

/**
 * URI形式を正規化する（iOSの file:/// 形式に対応）
 *
 * iOSでは file:///path/to/file （3スラッシュ）の形式を使用するが、
 * 一部の環境では file://path/to/file （2スラッシュ）が期待される
 * React Native #5308で報告されている問題に対応
 *
 * @param formData - 元のFormData
 * @returns URI形式が正規化されたFormData
 */
async function normalizeURIFormat(formData: FormData): Promise<FormData> {
  try {
    const newFormData = new FormData();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formDataWithParts = formData as any;
    if (
      Platform.OS !== "web" &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      formDataWithParts._parts != null &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      Array.isArray(formDataWithParts._parts)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const parts = formDataWithParts._parts as Array<[string, any]>;
      parts.forEach(([key, value]) => {
        if (
          typeof value === "object" &&
          value != null &&
          "uri" in value &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          typeof value.uri === "string"
        ) {
          const file = value as RNFileType;
          // eslint-disable-next-line prefer-destructuring
          let uri = file.uri;

          // iOSの file:/// を file:// に正規化
          // 例: file:///user/... → file://user/...
          if (uri.startsWith("file:///")) {
            uri = uri.replace("file:///", "file://");
          }
          // file://が無い場合は追加
          else if (
            !uri.startsWith("file://") &&
            !uri.startsWith("http://") &&
            !uri.startsWith("https://")
          ) {
            uri = `file://${uri}`;
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          newFormData.append(
            key,
            { ...file, uri } as RNFileType,
            file.name ?? "unknown"
          );
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          newFormData.append(key, value);
        }
      });
    }

    return newFormData;
  } catch (error) {
    await notify(
      {
        type: "normalizeURIFormat Error",
        searchableType: "NORMALIZE_URI_FORMAT_ERROR",
        error: {
          message: error instanceof Error ? error.message : String(error),
        },
      },
      { level: "warning", skipDeviceInfo: true }
    ).catch(() => {
      // 通知エラーは無視
    });
    return formData;
  }
}

/**
 * ファイル拡張子からMIME typeを推定する
 *
 * iOSのfile pickerが正しいMIME typeを提供しない問題に対応
 *
 * @param filename - ファイル名
 * @returns 推定されたMIME type
 */
function detectMIMETypeFromFilename(filename: string): string {
  const extension = filename.toLowerCase().split(".").pop();

  const mimeMap: Record<string, string> = {
    // 動画
    mp4: "video/mp4",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
    wmv: "video/x-ms-wmv",
    flv: "video/x-flv",
    webm: "video/webm",
    mkv: "video/x-matroska",
    m4v: "video/x-m4v",
    "3gp": "video/3gpp",

    // 画像
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    svg: "image/svg+xml",
    heic: "image/heic",
    heif: "image/heif",

    // 音声
    mp3: "audio/mpeg",
    wav: "audio/wav",
    m4a: "audio/mp4",
    aac: "audio/aac",
    ogg: "audio/ogg",
    flac: "audio/flac",

    // ドキュメント
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",

    // その他
    txt: "text/plain",
    json: "application/json",
    xml: "application/xml",
    zip: "application/zip",
    rar: "application/x-rar-compressed",
  };

  return mimeMap[extension ?? ""] ?? "application/octet-stream";
}

/**
 * 強化されたMIME型検出とFormData再構築
 *
 * iOSのfile pickerが正しいMIME typeを提供しない問題に対応し、
 * ファイル拡張子から適切なMIME typeを推定して設定
 *
 * @param formData - 元のFormData
 * @returns MIME型が強化されたFormData
 */
async function rebuildFormDataWithMIMEDetection(
  formData: FormData
): Promise<FormData> {
  try {
    const newFormData = new FormData();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formDataWithParts = formData as any;
    if (
      Platform.OS !== "web" &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      formDataWithParts._parts != null &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      Array.isArray(formDataWithParts._parts)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const parts = formDataWithParts._parts as Array<[string, any]>;
      parts.forEach(([key, value]) => {
        if (key === "operations" || key === "map") {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          newFormData.append(key, value);
        } else if (
          typeof value === "object" &&
          value != null &&
          "uri" in value
        ) {
          const file = value as RNFileType;
          // eslint-disable-next-line prefer-destructuring
          let uri = file.uri;

          // file:// プレフィックスを追加
          if (
            !uri.startsWith("file://") &&
            !uri.startsWith("http://") &&
            !uri.startsWith("https://")
          ) {
            uri = `file://${uri}`;
          }

          // MIME typeを強化：既存のtypeが不正確な場合はファイル名から推定
          const currentType = file.type;
          const detectedType =
            file.name != null ? detectMIMETypeFromFilename(file.name) : null;

          // 既存のtypeが存在しないか、application/octet-streamの場合は検出したtypeを使用
          const enhancedType =
            currentType == null ||
            currentType === "" ||
            currentType === "application/octet-stream"
              ? detectedType ?? "application/octet-stream"
              : currentType;

          const enhancedFile: RNFileType = {
            uri,
            name: file.name ?? `file_${Date.now()}.bin`,
            type: enhancedType,
          };

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
          newFormData.append(key, enhancedFile as any, enhancedFile.name);
        }
      });
    }

    return newFormData;
  } catch (error) {
    await notify(
      {
        type: "rebuildFormDataWithMIMEDetection Error",
        searchableType: "REBUILD_FORMDATA_MIME_ERROR",
        error: {
          message: error instanceof Error ? error.message : String(error),
        },
      },
      { level: "warning", skipDeviceInfo: true }
    ).catch(() => {
      // 通知エラーは無視
    });
    return formData;
  }
}
